import { useEffect, useRef, useState } from "react";
import { PICKER_LIMIT } from "../utils/constants";
/** How long the picker waits after the last keystroke before it searches (DB-876). */
export var PICKER_DEBOUNCE_MS = 300;
/**
 * Options of a search-as-you-type picker (DB-876): searches PICKER_DEBOUNCE_MS after the last keystroke, aborts the
 * previous request when the term changes and never shows a late answer to an older term. {@code truncated} says the
 * backend returned a full page (PICKER_LIMIT) - more records match than are shown, so the user should refine the term.
 *
 * @param term the typed term; empty clears the options without a request
 * @param fetchPage loads the options of a term, honouring the abort signal
 * @returns the options of the current term, whether they are loading, whether the page is full
 */
export function usePickerOptions(term, fetchPage) {
    var _a = useState([]), options = _a[0], setOptions = _a[1];
    var _b = useState(false), loading = _b[0], setLoading = _b[1];
    // the latest fetcher without restarting the search on every render
    var fetchPageRef = useRef(fetchPage);
    fetchPageRef.current = fetchPage;
    useEffect(function () {
        if (!term) {
            setOptions([]);
            setLoading(false);
            return undefined;
        }
        var controller = new AbortController();
        setLoading(true);
        var timer = setTimeout(function () {
            fetchPageRef
                .current(term, controller.signal)
                .then(function (result) {
                if (!controller.signal.aborted)
                    setOptions(result);
            })
                .catch(function () {
                if (!controller.signal.aborted)
                    setOptions([]);
            })
                .finally(function () {
                if (!controller.signal.aborted)
                    setLoading(false);
            });
        }, PICKER_DEBOUNCE_MS);
        return function () {
            clearTimeout(timer);
            controller.abort();
        };
    }, [term]);
    return { options: options, loading: loading, truncated: options.length >= PICKER_LIMIT };
}
